"use client";

import { useBrands } from "@/hooks/use-brands";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export interface PaymentMethodBrandRestrictionFieldsProps {
  restrictByBrands: boolean;
  allowedBrands: string[];
  onRestrictByBrandsChange: (value: boolean) => void;
  onAllowedBrandsChange: (brandIds: string[]) => void;
  description?: string;
}

export function PaymentMethodBrandRestrictionFields({
  restrictByBrands,
  allowedBrands,
  onRestrictByBrandsChange,
  onAllowedBrandsChange,
  description = "When enabled, this payment method only appears on receipt forms for the selected brands.",
}: PaymentMethodBrandRestrictionFieldsProps) {
  const { activeBrands } = useBrands();

  const toggleBrand = (brandId: string, checked: boolean) => {
    if (checked) {
      onAllowedBrandsChange(
        allowedBrands.includes(brandId)
          ? allowedBrands
          : [...allowedBrands, brandId]
      );
      return;
    }
    onAllowedBrandsChange(allowedBrands.filter((id) => id !== brandId));
  };

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label className="text-sm">Restrict by brand</Label>
          <p className="text-xs text-muted-foreground">
            {description}
          </p>
        </div>
        <Switch
          checked={restrictByBrands}
          onCheckedChange={(checked) => {
            onRestrictByBrandsChange(checked);
            if (!checked) onAllowedBrandsChange([]);
          }}
        />
      </div>

      {restrictByBrands ? (
        <div className="space-y-2 border-t pt-3">
          <Label className="text-sm">Allowed brands</Label>
          {activeBrands.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No active brands available.
            </p>
          ) : (
            <div className="grid gap-2">
              {activeBrands.map((brand) => {
                const brandId = brand.id ?? "";
                if (!brandId) return null;
                const checked = allowedBrands.includes(brandId);
                return (
                  <label
                    key={brandId}
                    htmlFor={`pm-brand-${brandId}`}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      id={`pm-brand-${brandId}`}
                      checked={checked}
                      onCheckedChange={(value) =>
                        toggleBrand(brandId, value === true)
                      }
                    />
                    <span>{brand.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
